import Button from '@material-ui/core/Button'
import Divider from '@material-ui/core/Divider'
import IconButton from '@material-ui/core/IconButton'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import Typography from '@material-ui/core/Typography'
import { grey, red } from '@material-ui/core/colors'
import { makeStyles } from '@material-ui/core/styles'
import AddIcon from '@material-ui/icons/Add'
import DeleteIcon from '@material-ui/icons/Delete'
import { filesize } from 'filesize'
import { useSnackbar } from 'notistack'
import { type ChangeEvent, useRef } from 'react'
import { useController } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { api } from '~/api'
import { useUploadToS3 } from '~/common/hooks'
import { MAX_IMAGE_UPLOAD_SIZE } from '~/consts'
import { useProductFormContext } from '~/products/hooks/useProductForm'
import { useAppSelector } from '~/store'

import type { Attribute, ChannelAttributeValueName } from './types'

type Props = {
    name: ChannelAttributeValueName
    attribute: Attribute
}

const useStyles = makeStyles(() => ({
    productCertification: {
        padding: 16,
        '& td a': {
            color: grey[900],
        },
    },
}))

export default function AttributeProductCertification({ name, attribute }: Props) {
    const { t } = useTranslation('product_attributes')

    const staticFilesDomain = useAppSelector((state) => state.initial.staticFilesDomain)
    const { control, getValues } = useProductFormContext()
    const { field } = useController({
        control,
        name,
        defaultValue: '',
        rules: {
            required: attribute.required && t('field_is_required', { name: attribute.name }),
            validate: (value) => {
                if (!value && attribute.required) {
                    return t('field_is_required', { name: attribute.name })
                }
                return true
            },
        },
    })

    const attributeValue = field.value || []

    const uploadToS3 = useUploadToS3()
    const getPresignedUrl = api.stores.getPresignedUrl.useMutation()
    const { enqueueSnackbar } = useSnackbar()

    const fileInputRef = useRef<HTMLInputElement>(null)

    function updateFieldValue(newValue) {
        field.onChange(newValue)
    }

    function handleUpload(e: ChangeEvent<HTMLInputElement>) {
        if (!e.target.files?.length) {
            return
        }

        const files = Array.from(e.target.files)
        if (files.some((file) => file.size >= MAX_IMAGE_UPLOAD_SIZE)) {
            enqueueSnackbar(
                t('file_is_too_large', { max_size: filesize(MAX_IMAGE_UPLOAD_SIZE, { base: 2 }) }),
                { variant: 'error' }
            )
            return
        }

        const file = files[0]
        if (!file) {
            return
        }

        const fileName = file.name

        return getPresignedUrl.mutate(
            {
                body: [{ filename: file.name, content_type: file.type }],
                params: { storeId: getValues().store.id },
            },
            {
                onSuccess: (data) => {
                    if (data.body.length !== 1) {
                        return
                    }

                    const { filename, presigned_url } = data.body[0]

                    uploadToS3.mutate(
                        { file, presignedUrl: presigned_url },
                        {
                            onSuccess: () => {
                                updateFieldValue([
                                    ...attributeValue,
                                    {
                                        file_url: `${staticFilesDomain}${filename}`,
                                        file_name: fileName,
                                    },
                                ])
                            },
                            onError: (error) => {
                                enqueueSnackbar(error.message ?? t('failed_to_upload_image'), {
                                    variant: 'error',
                                })
                            },
                        }
                    )
                },
                onError: (error) => {
                    enqueueSnackbar(
                        error.body instanceof Error
                            ? error.body.message
                            : t('failed_to_get_presigned_url'),
                        { variant: 'error' }
                    )
                },
            }
        )
    }

    function onFileDelete(index: number) {
        attributeValue.splice(index, 1)
        updateFieldValue(attributeValue)
    }

    const classes = useStyles()

    return (
        <div>
            <Divider />
            <div className={classes.productCertification}>
                <Typography gutterBottom>
                    <span>
                        {t('product_certifications')} - {attribute.name}
                    </span>
                    {attribute.required && (
                        <span style={{ marginLeft: 4, color: red[500] }}>*</span>
                    )}
                </Typography>

                {attributeValue.length > 0 && (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('file_name')}</TableCell>
                                <TableCell>{t('remove')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {attributeValue.map((item, itemIndex) => (
                                <TableRow key={itemIndex}>
                                    <TableCell>
                                        <a href={item.file_url} target="_blank">
                                            {item.file_name}
                                        </a>
                                    </TableCell>
                                    <TableCell>
                                        <IconButton
                                            color="secondary"
                                            onClick={() => onFileDelete(itemIndex)}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}

                <Button
                    startIcon={<AddIcon />}
                    disabled={getPresignedUrl.isLoading || uploadToS3.isLoading}
                    onClick={() => fileInputRef.current?.click()}
                >
                    {t('upload_file')}
                </Button>

                <input
                    hidden
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,application/pdf"
                    onChange={handleUpload}
                />
            </div>
        </div>
    )
}
